import { Link } from "react-router-dom";

export function Timeline({ steps, testid }) {
  return (
    <ol className="relative grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid={testid}>
      {steps.map((s, i) => {
        const Icon = s.icon;
        return (
          <li key={s.title} className="card fade-up relative p-6 sm:p-7" style={{ animationDelay: `${i * 70}ms` }}>
            <div className="flex items-center gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-teal font-display text-lg font-bold text-on-teal" aria-hidden="true">{i + 1}</span>
              {Icon && <Icon className="text-amber-ink" size={26} aria-hidden="true" />}
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold">
              <span className="sr-only">Step {i + 1}: </span>{s.title}
            </h3>
            <p className="mt-2 text-ink-muted">{s.body}</p>
          </li>
        );
      })}
    </ol>
  );
}

export function PageHero({ eyebrow, title, intro, children, image, imageAlt }) {
  return (
    <section className="border-b border-line bg-surface">
      <div className={`container-x grid items-center gap-10 py-14 lg:py-20 ${image ? "lg:grid-cols-[1.2fr_1fr]" : ""}`}>
        <div className="fade-up max-w-3xl">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-teal sm:text-5xl">{title}</h1>
          {intro && <p className="mt-5 text-lg text-ink-muted sm:text-xl">{intro}</p>}
          {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
        </div>
        {image && (
          <img src={image} alt={imageAlt} className="fade-up aspect-[4/3] w-full rounded-[2rem] object-cover" style={{ animationDelay: "120ms" }} />
        )}
      </div>
    </section>
  );
}

export function Section({ id, eyebrow, title, intro, children, className = "", tone = "" }) {
  return (
    <section id={id} className={`${tone} py-16 lg:py-24 ${className}`} aria-labelledby={id ? `${id}-title` : undefined}>
      <div className="container-x">
        {(title || eyebrow) && (
          <div className="mb-10 max-w-3xl">
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            {title && <h2 id={id ? `${id}-title` : undefined} className="mt-2 font-display text-3xl font-semibold leading-snug sm:text-4xl">{title}</h2>}
            {intro && <p className="mt-4 text-lg text-ink-muted">{intro}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

export function CtaBand({ title = "Ready to get started?", text }) {
  return (
    <section className="py-16">
      <div className="container-x">
        <div className="flex flex-col gap-6 rounded-[2rem] bg-teal p-8 text-on-teal sm:p-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-semibold">{title}</h2>
            <p className="mt-3 text-lg opacity-90">{text || "Talk to us about an equipment trial or a home modification. Participants, families and professionals are all welcome."}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/request-trial" className="btn bg-on-teal text-teal hover:opacity-90" data-testid="cta-band-trial-btn">Request a Trial</Link>
            <Link to="/referrers#referral-form" className="btn-accent" data-testid="cta-band-referral-btn">Make a Referral</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Disclaimer({ children }) {
  return (
    <aside className="rounded-2xl border-l-4 border-amber bg-amber-soft p-5 text-ink" aria-label="Important information">
      {children || "Funding decisions are made by the NDIA. Equipment and modification recommendations should be made with your treating therapist."}
    </aside>
  );
}
